import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { MapService } from 'src/app/services';
import { Coords, TechnicalService } from 'src/app/interfaces';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Angular Google Map component wrapper
 *
 * @export
 * @class MapComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnInit, OnDestroy {
  /**
   * Technical services to show in the map
   *
   * @type {TechnicalService[]}
   * @memberof MapComponent
   */
  @Input() technicalServices: TechnicalService[] = [];

  /**
   * Emit an event with selected Technical Service
   *
   * @type {EventEmitter<TechnicalService>}
   * @memberof MapComponent
   */
  @Output() markerClick: EventEmitter<TechnicalService> = new EventEmitter<TechnicalService>();

  /**
   * Maps coords
   *
   * @type {Coords}
   * @memberof MapComponent
   */
  coords: Coords;

  /**
   * Zoom
   *
   * @type {number}
   * @memberof MapComponent
   */
  zoom: number;

  /**
   * Max map zoom
   *
   * @type {number}
   * @memberof MapComponent
   */
  maxZoom: number = environment.google.maps.maxZoom;

  /**
   * Index of the marker window opened
   *
   * @type {number}
   * @memberof MapComponent
   */
  openedWindow: number;

  /**
   * Marker configuration
   *
   * @memberof MapComponent
   */
  icon = {
    url: 'assets/images/marker.svg',
    scaledSize: {
      width: 40,
      height: 40,
    },
  };

  /**
   * Current location Marker configuration
   *
   * @memberof MapComponent
   */
  currentLocationIcon = {
    url: 'assets/images/current-marker.svg',
    scaledSize: {
      width: 40,
      height: 40,
    },
  };

  /**
   * Collect all Observable subscriptions
   *
   * @private
   * @type {Subscription}
   * @memberof MapComponent
   */
  private subscriptions: Subscription = new Subscription();

  /**
   * Creates an instance of MapComponent.
   * @param {MapService} mapService
   * @memberof MapComponent
   */
  constructor(private mapService: MapService) {}

  /**
   * Map init is async
   *
   * @param {*} map
   * @memberof MapComponent
   */
  onMapReady(map: any) {
    // this.mapService.map = map;
    this.mapService.setMap(map);
  }

  /**
   * Listen for zoom and coords changes
   *
   * @memberof MapComponent
   */
  async ngOnInit() {
    this.subscriptions.add(this.mapService.zoom$.subscribe((zoom: number) => (this.zoom = zoom)));
    this.subscriptions.add(this.mapService.coords$.subscribe((coords: Coords) => this.onCoordsChanged(coords)));
  }

  /**
   * Update map coords
   *
   * @private
   * @param {Coords} coords
   * @memberof MapComponent
   */
  private onCoordsChanged(coords: Coords): void {
    this.coords = { ...coords };
    this.mapService.panTo(this.coords);
    this.openedWindow = null;
  }

  /**
   * Track by technical service id
   *
   * @param {TechnicalService} technicalService
   * @returns
   * @memberof MapComponent
   */
  trackById(technicalService: TechnicalService) {
    return technicalService.id;
  }

  /**
   * Open window of the marker clicked
   *
   * @param {TechnicalService} technicalService
   * @memberof MapComponent
   */
  clickedMarker(technicalService: TechnicalService) {
    this.setOpenedWindow(technicalService.id);

    this.markerClick.emit(technicalService);
  }

  /**
   * Remove all subscriptions
   *
   * @memberof MapComponent
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Open marker info window
   *
   * @private
   * @param {number} id
   * @memberof MapComponent
   */
  private setOpenedWindow(id: number) {
    this.technicalServices = this.technicalServices.map((technicalService: TechnicalService) => {
      technicalService.isOpened = technicalService.id === id;

      return technicalService;
    });
  }
}
